import { Component, inject, ChangeDetectionStrategy } from '@angular/core';

import { MatButtonModule } from '@angular/material/button';
import { MatDialogModule, MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { Observable } from 'rxjs';
import { RosterImportResult } from '@k12/models';

export interface RosterImportDialogData {
  importQuery: (file: File) => Observable<RosterImportResult>;
}

@Component({
  changeDetection: ChangeDetectionStrategy.Eager,
  selector: 'lib-roster-import-dialog',
  templateUrl: './roster-import-dialog.component.html',
  styleUrl: './roster-import-dialog.component.scss',
  standalone: true,
  imports: [MatButtonModule, MatDialogModule, MatIconModule, MatProgressBarModule]
})
export class RosterImportDialogComponent {
  phase: 'idle' | 'processing' | 'done' = 'idle';
  result: RosterImportResult | null = null;
  importError: string | null = null;
  xlsxError: string | null = null;

  dialogRef = inject(MatDialogRef<RosterImportDialogComponent>);
  data = inject<RosterImportDialogData>(MAT_DIALOG_DATA);

  constructor() {}

  onFileSelected(event: Event): void {
    const file = (event.target as HTMLInputElement).files?.[0];
    if (!file) return;
    if (!file.name.toLowerCase().endsWith('.xlsx')) {
      this.xlsxError = 'Only .xlsx files are accepted. Please select an .xlsx file exported from this roster.';
      (event.target as HTMLInputElement).value = '';
      return;
    }
    this.xlsxError = null;
    this.phase = 'processing';
    this.importError = null;
    this.result = null;
    this.data.importQuery(file).subscribe({
      next: (result) => {
        this.result = result;
        this.phase = 'done';
      },
      error: () => {
        this.importError = 'An unexpected error occurred. Please try again.';
        this.phase = 'done';
      }
    });
    // Reset input so the same file can be re-selected after an error
    (event.target as HTMLInputElement).value = '';
  }

  closeAndRefresh(): void {
    this.dialogRef.close(this.result);
  }

  get hasErrors(): boolean {
    return (this.result?.errors?.length ?? 0) > 0;
  }

  get errorText(): string {
    return (this.result?.errors ?? [])
      .map((e) => `PID ${e.pid || '?'} (row ${e.rowNumber}): ${e.errorMessage}`)
      .join('\n');
  }
}
